import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Order } from '../../types/order';
import { FavoriteOrder } from '../../services/favoriteService';
import { DateFilter } from '../../utils/orderUtils';
import UserOrdersStatusTabs from './UserOrdersStatusTabs';
import UserOrdersFilters from './UserOrdersFilters';
import { UserOrdersHeader } from './UserOrdersHeader';
import { UserOrdersContent } from './UserOrdersContent';
import { UserOrdersModals } from './UserOrdersModals';

interface UserOrdersLayoutProps {
  // Authentication & User
  isAuthenticated: boolean;
  userInfo: any;

  // Data
  orders: Order[];
  filteredOrders: Order[];
  favoriteOrders: FavoriteOrder[];
  favoriteIds: Set<number>;

  // Loading states
  loading: boolean;
  favoritesLoading: boolean;

  // Errors
  error: any;
  favoritesError: any;

  // Filters
  statusFilter: string;
  searchTerm: string;
  dateFilter: DateFilter;
  hasFilters: boolean;

  // Counts
  favoritesCount: number;
  getStatusCount: (status: string) => number;

  // Modal state
  selectedOrder: Order | null;
  selectedFavorite: FavoriteOrder | null;

  // Computed
  getEmptyStateType: () => 'error' | 'no-orders' | 'no-filtered-results' | 'no-favorites';
  getFavoritesEmptyStateType: () => 'error' | 'no-orders' | 'no-filtered-results' | 'no-favorites';

  // Handlers - Navigation
  handleGoHome: () => void;

  // Handlers - Filters
  setStatusFilter: (status: string) => void;
  setSearchTerm: (term: string) => void;
  setDateFilter: (filter: DateFilter) => void;

  // Handlers - Orders
  handleOrderDetailClick: (order: Order) => void;
  handleReorder: (order: Order) => void;
  toggleFavorite: (orderId: number) => void;

  // Handlers - Favorites
  handleRemoveFavorite: (orderId: number) => void;
  handleToggleFavoriteFromList: (orderId: number) => void;
  handleFavoriteReorder: (favorite: FavoriteOrder) => void;
  handleFavoriteDetailClick: (favorite: FavoriteOrder) => void;

  // Handlers - Modals
  handleCloseOrderModal: () => void;
  handleCloseFavoriteModal: () => void;
  handleFavoriteModalReorder: (order: Order) => void;

  // Retry handlers
  fetchUserOrders: () => void;
  fetchFavorites: () => void;
}

export const UserOrdersLayout = memo<UserOrdersLayoutProps>(
  ({
    // Authentication & User
    isAuthenticated,
    userInfo,

    // Data
    orders,
    filteredOrders,
    favoriteOrders,
    favoriteIds,

    // Loading states
    loading,
    favoritesLoading,

    // Errors
    error,
    favoritesError,

    // Filters
    statusFilter,
    searchTerm,
    dateFilter,
    hasFilters,

    // Counts
    favoritesCount,
    getStatusCount,

    // Modal state
    selectedOrder,
    selectedFavorite,

    // Computed
    getEmptyStateType,
    getFavoritesEmptyStateType,

    // Handlers - Navigation
    handleGoHome,

    // Handlers - Filters
    setStatusFilter,
    setSearchTerm,
    setDateFilter,

    // Handlers - Orders
    handleOrderDetailClick,
    handleReorder,
    toggleFavorite,

    // Handlers - Favorites
    handleRemoveFavorite,
    handleToggleFavoriteFromList,
    handleFavoriteReorder,
    handleFavoriteDetailClick,

    // Handlers - Modals
    handleCloseOrderModal,
    handleCloseFavoriteModal,
    handleFavoriteModalReorder,

    // Retry handlers
    fetchUserOrders,
    fetchFavorites,
  }) => {
    // Authentication check
    if (!isAuthenticated || !userInfo) {
      return null;
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-yellow-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <UserOrdersHeader
            onGoHome={handleGoHome}
            totalOrdersCount={orders.length}
            orders={orders}
          />

          {/* Status Tabs */}
          <UserOrdersStatusTabs
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            getStatusCount={getStatusCount}
            totalCount={orders.length}
            favoritesCount={favoritesCount}
          />

          {/* Filtreler - Sadece favoriler sekmesinde değilse göster */}
          {statusFilter !== 'favorites' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="mb-8"
            >
              <UserOrdersFilters
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                dateFilter={dateFilter}
                onDateFilterChange={setDateFilter}
              />
            </motion.div>
          )}

          {/* İçerik */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <UserOrdersContent
              // Data
              filteredOrders={filteredOrders}
              favoriteOrders={favoriteOrders}
              favoriteIds={favoriteIds}
              // State
              statusFilter={statusFilter}
              loading={loading}
              favoritesLoading={favoritesLoading}
              // Error handling
              error={error}
              favoritesError={favoritesError}
              getEmptyStateType={getEmptyStateType}
              getFavoritesEmptyStateType={getFavoritesEmptyStateType}
              hasFilters={hasFilters}
              // Handlers - Orders
              onOrderDetailClick={handleOrderDetailClick}
              onReorder={handleReorder}
              onFavoriteToggle={toggleFavorite}
              // Handlers - Favorites
              onRemoveFavorite={handleRemoveFavorite}
              onFavoriteReorder={handleFavoriteReorder}
              onFavoriteDetailClick={handleFavoriteDetailClick}
              onToggleFavoriteFromList={handleToggleFavoriteFromList}
              // Retry handlers
              onRetryOrders={fetchUserOrders}
              onRetryFavorites={fetchFavorites}
            />
          </motion.div>

          {/* Modals */}
          <UserOrdersModals
            // Order Modal
            selectedOrder={selectedOrder}
            onCloseOrderModal={handleCloseOrderModal}
            // Favorite Modal
            selectedFavorite={selectedFavorite}
            onCloseFavoriteModal={handleCloseFavoriteModal}
            // Shared props
            favoriteIds={favoriteIds}
            onReorder={handleReorder}
            onFavoriteToggle={toggleFavorite}
            onFavoriteModalReorder={handleFavoriteModalReorder}
          />
        </div>
      </div>
    );
  }
);

UserOrdersLayout.displayName = 'UserOrdersLayout';
